// Une écriture Supabase peut échouer SANS erreur.
//
// Quand la RLS refuse un UPDATE ou un DELETE, PostgREST ne renvoie pas d'erreur :
// il renvoie « 0 ligne touchée » avec `error === null`. Un code qui ne teste que
// `if (error)` conclut donc au succès, affiche un message de confirmation, met à
// jour son état local — et n'a rien écrit. Au remontage de l'écran, la valeur
// d'avant réapparaît.
//
// C'est le motif « ça dit enregistré, ça ne se sauvegarde pas » rapporté en
// production par Coiffure La Nuque le 5 août 2026.
//
// L'anti-régression correspondante est déjà écrite dans le dépôt :
// `docs/features/calendrier-rdv/2026-06-27-disponibilite-employe-secondaire-multi-service.md:30`
// (« toujours épingler la FK ET capturer `error` »). Ce module lui donne un point
// de passage commun : sans `.select()`, on ne PEUT pas savoir si l'écriture a eu
// lieu, donc `.select()` n'est pas facultatif sur un chemin d'écriture.

/**
 * L'écriture a-t-elle réellement touché au moins une ligne ?
 *
 * À utiliser sur le `data` d'un `.update()` / `.delete()` suivi d'un `.select()`.
 * `null`, `undefined` et le tableau vide valent tous « refusée ».
 */
export function writeLanded(data: unknown): boolean {
  if (data === null || data === undefined) return false;
  if (Array.isArray(data)) return data.length > 0;
  return true;
}

/**
 * Erreur à lever (ou à passer à `errorToast`) quand une écriture est refusée en
 * silence. Porte un `code` reconnaissable pour distinguer ce cas d'une panne
 * réseau dans les journaux.
 */
export class SilentWriteRefusedError extends Error {
  readonly code = 'WRITE_REFUSED_NO_ROWS';

  constructor(table: string) {
    super(
      `L'écriture sur "${table}" n'a touché aucune ligne : refus RLS probable, ` +
        `ou la ligne visée n'existe pas.`
    );
    this.name = 'SilentWriteRefusedError';
  }
}

// ───────────────────────────────────────────────────────────────────────────────
// Juger une écriture sur les lignes qu'elle a REELLEMENT touchées
// ───────────────────────────────────────────────────────────────────────────────
//
// `writeLanded` ci-dessus ne regarde que `data`. Les deux règles ci-dessous
// prennent le résultat entier, erreur comprise, et se distinguent l'une de
// l'autre par la façon dont l'écriture désigne sa cible. Zéro ligne ne veut pas
// dire la même chose dans les deux cas.

type WriteResult = { error: unknown; data: unknown[] | null };

/**
 * Règle des écritures qui visent UNE ligne par identifiant (`.eq('id', ...)`).
 * La ligne existe, l'écran vient de l'afficher : zéro ligne touchée ne peut être
 * qu'un refus. Demander `.select()` sur l'écriture reste indispensable, sans quoi
 * `data` est toujours nul et tout ressemble à un refus.
 */
export const writeWasRefused = (result: WriteResult): boolean =>
  result.error !== null || !writeLanded(result.data);

/**
 * Règle des écritures qui visent des lignes par CRITERE (`.eq('section', nom)`).
 * Zéro ligne y est parfaitement légitime quand le critère ne désigne rien, par
 * exemple une section qui ne contient aucun service. Appliquer la règle stricte
 * inventerait un échec là où tout s'est bien passé, c'est à dire le même genre de
 * message trompeur que celui qu'on cherche à supprimer.
 * La propriété juste est le nombre de lignes attendu, que l'écran connaît déjà
 * puisqu'il affiche la liste concernée.
 *
 * La comparaison est volontairement `<` et non `!==`. Les deux appelants épinglent
 * leur écriture sur le salon courant, donc PLUS de lignes touchées que prévu ne
 * peut jamais être un refus : c'est la liste de l'écran qui avait vieilli, parce
 * qu'un service a été ajouté entre le chargement de l'écran et l'écriture. Crier
 * à l'échec dans ce cas serait un mensonge, tout a suivi. La forme `<` attrape
 * exactement les mêmes refus que la forme stricte, sans cette fausse alerte.
 */
export const writeMissedRows = (result: WriteResult, expectedRows: number): boolean =>
  result.error !== null || (result.data?.length ?? 0) < expectedRows;
